import axios from 'axios';

export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const AUTH_REFRESH_FAILED_EVENT = 'auth:refresh-failed';

export type AuthTokenResponse = {
  status: 'ok';
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
};

type RetriableRequestConfig = {
  _retry?: boolean;
  headers?: any;
};

export const getAccessToken = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

export const storeAuthTokens = (tokens: AuthTokenResponse) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
  window.sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
};

export const clearAuthTokens = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

const notifyAuthRefreshFailed = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(AUTH_REFRESH_FAILED_EVENT));
};

const normalizeDetail = (detail: unknown): string | undefined => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const items = detail
      .map((item) => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
          const loc =
            Array.isArray((item as any).loc) && (item as any).loc.length
              ? `(${(item as any).loc.join(' >')}) `
              : '';
          if (typeof (item as any).msg === 'string') return `${loc}${(item as any).msg}`;
          try {
            return `${loc}${JSON.stringify(item)}`;
          } catch {
            return loc.trim();
          }
        }
        return String(item);
      })
      .filter(Boolean);
    if (items.length) return items.join('; ');
  }
  if (detail && typeof detail === 'object') {
    const msg = (detail as any).msg;
    const loc =
      Array.isArray((detail as any).loc) && (detail as any).loc.length
        ? `(${(detail as any).loc.join(' >')}) `
        : '';
    if (typeof msg === 'string') return `${loc}${msg}`;
    try {
      return JSON.stringify(detail);
    } catch {
      return loc.trim() || undefined;
    }
  }
  if (detail == null) return undefined;
  return String(detail);
};


const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

const integrationApiBaseURL = (() => {
  const baseURL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  if (!baseURL) return '/api';
  return baseURL.endsWith('/api') ? baseURL : `${baseURL}/api`;
})();

const authApi = axios.create({
  baseURL: integrationApiBaseURL,
  withCredentials: true,
});

const validateTokenResponse = (data: AuthTokenResponse) => {
  if (
    data?.status !== 'ok' ||
    !data.access_token ||
    !data.refresh_token
  ) {
    throw new Error('Invalid token response');
  }
  return data;
};

export async function refreshAuthTokens(refreshToken: string) {
  const { data } = await authApi.post<AuthTokenResponse>('/auth/refresh_token', {
    refresh_token: refreshToken,
  });
  const tokens = validateTokenResponse(data);
  storeAuthTokens(tokens);
  return tokens;
}

let refreshPromise: Promise<AuthTokenResponse> | null = null;

async function refreshStoredAuthTokens() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('Refresh token is missing');

  if (!refreshPromise) {
    refreshPromise = refreshAuthTokens(refreshToken).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

api.interceptors.request.use(config => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    if (error.response) {
      const isAiReportRequest = /^\/api\/v1\/(ai-reports(?:\/|$)|ai-report-settings(?:\?|$))/.test(error.config?.url || '');
      // responseType=text нужен для HTML, но ошибки этих ручек всё равно содержат JSON.
      if (isAiReportRequest && typeof error.response.data === 'string') {
        try { error.response.data = JSON.parse(error.response.data); } catch {}
      }
      const rawDetail = error.response.data?.detail;
      const normalizedDetail = normalizeDetail(rawDetail);
      if (!isAiReportRequest && normalizedDetail && normalizedDetail !== rawDetail) {
        error.response.data.detail = normalizedDetail;
      }

      if (error.response.status === 401) {
        const originalRequest = error.config as typeof error.config & RetriableRequestConfig;
        if (!originalRequest?._retry && getRefreshToken()) {
          originalRequest._retry = true;

          try {
            const tokens = await refreshStoredAuthTokens();
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
            return api(originalRequest);
          } catch (refreshError) {
            clearAuthTokens();
            notifyAuthRefreshFailed();
            return Promise.reject(refreshError);
          }
        }
      }

      if (error.response.status === 403) {
        if (isAiReportRequest && rawDetail?.code === 'ai_reports_disabled') {
          return Promise.reject(error);
        }
        const details = normalizedDetail || 'У вас нет прав для посещения данной страницы';
        window.location.href = `/403?detail=${encodeURIComponent(details)}`;
      } else if (error.response.status === 404) {
        const expectsJson = !error.config || !error.config.responseType || error.config.responseType === 'json';
        if (!expectsJson && !isAiReportRequest) {
          window.location.href = '/*';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
